import React, { useEffect, useMemo, useRef, useState } from "react";
import { Button, Card, CardBody, Col, Input, Label, Row } from "reactstrap";
import { toast } from "react-toastify";

import LoadingSpinner from "../../../../Components/Common/Spinner";
import TableContainer from "../../../../Components/Common/TableContainer";
import AppModal from "../../../../Components/Common/AppModal";
import DeleteModal from "../../../../Components/Common/DeleteModal";
import SettingsPanel from "../../components/SettingsPanel";

import {
  listMyApiKeys,
  createMyApiKeys,
  voidMyApiKeys,
  getMyApiKeyData,
  updateMyApiKeys,
  listAuthCapabilities,
} from "./apiKeysService";

// Company Settings > API Keys.
//
// Manages the signed-in user's master-system API keys via MasterApiKeyController
// (masterapikey/my/list|create|update|void). Keys are returned masked; the full
// plaintext key is only ever shown ONCE, in the reveal modal at creation time.
//
// Access scope (task 20751): a key is either "Full access" (FlagAllAccess=1, the
// default and every pre-existing key) or "Restricted" to an explicit list of
// auth capabilities, chosen from a searchable click-to-toggle table. The toggle
// + picker appear both when creating a key and when editing an existing key.
//
// Creation/edit is gated server-side (AUTHCAP465X / AUTHCAP2602X), so a user
// without permission gets a denial from the backend surfaced as an error toast.

// Rows expose their id as either MasterApiKey_Id (list) or Id (create response).
const keyId = (row) => row?.MasterApiKey_Id ?? row?.Id;

// Cap how many rows render at once so clicking stays snappy on large capability
// sets; the user narrows with the search box.
const MAX_VISIBLE_CAPS = 400;

const formatDateTime = (value) => {
  if (!value) return "-";
  const d = new Date(value.replace(" ", "T"));
  return isNaN(d.getTime()) ? value : d.toLocaleString();
};

// Searchable, click-to-toggle capability table. selectedIds/onChange work in
// plain capability-id numbers.
function CapabilityPicker({ capabilities, selectedIds, onChange }) {
  const [search, setSearch] = useState("");
  const selectedSet = useMemo(() => new Set(selectedIds), [selectedIds]);

  const filtered = useMemo(() => {
    const q = search.trim().toLowerCase();
    if (!q) return capabilities;
    return capabilities.filter((c) => {
      const code = `authcap${c.Id}x`;
      return (
        String(c.Name || "").toLowerCase().includes(q) ||
        String(c.Description || "").toLowerCase().includes(q) ||
        code.includes(q) ||
        String(c.Id).includes(q)
      );
    });
  }, [capabilities, search]);

  const visible = filtered.slice(0, MAX_VISIBLE_CAPS);
  const overflow = filtered.length - visible.length;

  // Header tickbox state: reflects the whole filtered set (not just the visible
  // slice), so ticking it selects every match even beyond the render cap.
  const allFilteredSelected =
    filtered.length > 0 && filtered.every((c) => selectedSet.has(c.Id));
  const someFilteredSelected = filtered.some((c) => selectedSet.has(c.Id));

  const headerRef = useRef(null);
  useEffect(() => {
    if (headerRef.current) {
      headerRef.current.indeterminate =
        someFilteredSelected && !allFilteredSelected;
    }
  }, [someFilteredSelected, allFilteredSelected]);

  const toggle = (id) =>
    onChange(
      selectedSet.has(id)
        ? selectedIds.filter((x) => x !== id)
        : [...selectedIds, id]
    );

  // Header tickbox: tick -> select all filtered; untick -> remove all filtered.
  const toggleAllFiltered = () => {
    const filteredIds = new Set(filtered.map((c) => c.Id));
    if (allFilteredSelected) {
      onChange(selectedIds.filter((id) => !filteredIds.has(id)));
    } else {
      const next = new Set(selectedIds);
      filteredIds.forEach((id) => next.add(id));
      onChange([...next]);
    }
  };

  const clearAll = () => onChange([]);

  return (
    <div>
      <div className="d-flex align-items-center gap-2 mb-2">
        <div className="position-relative flex-grow-1">
          <Input
            type="text"
            className="form-control"
            style={{ paddingRight: "2rem" }}
            placeholder="Search by name or AUTHCAP code…"
            value={search}
            autoFocus
            onChange={(e) => setSearch(e.target.value)}
          />
          {search ? (
            <i
              className="ri-close-line position-absolute text-muted"
              role="button"
              title="Clear search"
              onClick={() => setSearch("")}
              style={{
                top: "50%",
                right: "0.6rem",
                transform: "translateY(-50%)",
                cursor: "pointer",
                fontSize: "1.1rem",
              }}
            />
          ) : null}
        </div>
        <Button
          color="danger"
          size="sm"
          className="text-nowrap"
          onClick={clearAll}
          disabled={!selectedIds.length}
        >
          <i className="ri-close-circle-line align-bottom me-1" /> Remove All
        </Button>
      </div>

      <div className="d-flex justify-content-between text-muted fs-12 mb-1">
        <span>
          <strong>{selectedIds.length}</strong> selected
        </span>
        <span>{filtered.length} shown</span>
      </div>

      <div
        className="table-responsive border rounded"
        style={{ maxHeight: 340, overflowY: "auto" }}
      >
        <table className="table table-sm table-hover align-middle mb-0">
          <thead
            className="table-light"
            style={{ position: "sticky", top: 0, zIndex: 1 }}
          >
            <tr>
              <th style={{ width: 40 }}>
                <Input
                  type="checkbox"
                  className="form-check-input"
                  innerRef={headerRef}
                  checked={allFilteredSelected}
                  onChange={toggleAllFiltered}
                  disabled={!filtered.length}
                  title="Tick all shown capabilities"
                />
              </th>
              <th>Capability</th>
              <th style={{ width: 150 }}>Code</th>
            </tr>
          </thead>
          <tbody>
            {visible.map((c) => {
              const checked = selectedSet.has(c.Id);
              return (
                <tr
                  key={c.Id}
                  onClick={() => toggle(c.Id)}
                  style={{ cursor: "pointer" }}
                  className={checked ? "table-active" : ""}
                >
                  <td onClick={(e) => e.stopPropagation()}>
                    <Input
                      type="checkbox"
                      className="form-check-input"
                      checked={checked}
                      onChange={() => toggle(c.Id)}
                    />
                  </td>
                  <td>
                    <div className="fw-medium">{c.Name || "Capability"}</div>
                    {c.Description ? (
                      <div className="text-muted fs-12">{c.Description}</div>
                    ) : null}
                  </td>
                  <td>
                    <code className="text-muted">AUTHCAP{c.Id}X</code>
                  </td>
                </tr>
              );
            })}
            {!filtered.length ? (
              <tr>
                <td colSpan={3} className="text-muted text-center py-3">
                  No capabilities match “{search}”.
                </td>
              </tr>
            ) : null}
            {overflow > 0 ? (
              <tr>
                <td colSpan={3} className="text-muted text-center py-2 fs-12">
                  {overflow} more — refine your search to narrow the list.
                </td>
              </tr>
            ) : null}
          </tbody>
        </table>
      </div>
    </div>
  );
}

export default function ApiKeysSettings() {
  const [keys, setKeys] = useState([]);
  const [loading, setLoading] = useState(true);

  // Auth-capability pool, loaded lazily the first time a modal needs it.
  // null = not yet loaded.
  const [capabilities, setCapabilities] = useState(null);

  // Create
  const [createOpen, setCreateOpen] = useState(false);
  const [label, setLabel] = useState("");
  const [fullAccess, setFullAccess] = useState(true);
  const [selectedCapIds, setSelectedCapIds] = useState([]);
  const [creating, setCreating] = useState(false);

  // Edit access
  const [editOpen, setEditOpen] = useState(false);
  const [editTarget, setEditTarget] = useState(null);
  const [editFullAccess, setEditFullAccess] = useState(true);
  const [editSelectedCapIds, setEditSelectedCapIds] = useState([]);
  const [editLoading, setEditLoading] = useState(false);
  const [editSaving, setEditSaving] = useState(false);

  // Full plaintext key shown once after creation (null when the modal is closed).
  const [revealKey, setRevealKey] = useState(null);

  const [deleteTarget, setDeleteTarget] = useState(null);
  const [deleting, setDeleting] = useState(false);

  const fetchKeys = async () => {
    setLoading(true);
    const res = await listMyApiKeys();
    if (res?.status === 200) {
      setKeys(res.masterapikey_list ?? []);
    } else if (res) {
      toast.error(res.message || "Failed to load API keys");
    }
    setLoading(false);
  };

  useEffect(() => {
    fetchKeys();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Load the capability pool once and cache it. Returns the list either way.
  const ensureCapabilities = async () => {
    if (capabilities !== null) return capabilities;
    const res = await listAuthCapabilities();
    const list =
      res?.status === 200 ? res.mastersecurityauthcapability_list ?? [] : [];
    if (res && res.status !== 200) {
      toast.error(res.message || "Could not load auth capabilities");
    }
    setCapabilities(list);
    return list;
  };

  const openCreate = () => {
    setLabel("");
    setFullAccess(true);
    setSelectedCapIds([]);
    setCreateOpen(true);
    ensureCapabilities();
  };

  const handleCreate = async () => {
    const trimmed = label.trim();
    if (!trimmed) return;
    if (!fullAccess && selectedCapIds.length === 0) {
      toast.error("Pick at least one capability, or switch on Full access");
      return;
    }
    setCreating(true);
    const res = await createMyApiKeys([
      {
        Label: trimmed,
        FlagAllAccess: fullAccess ? 1 : 0,
        AuthCapabilityIds: fullAccess ? [] : selectedCapIds,
      },
    ]);
    setCreating(false);
    if (res?.status === 200) {
      const full = res.masterapikey_list?.[0]?.ApiKey;
      setCreateOpen(false);
      setLabel("");
      toast.success(res.message || "API key created");
      if (full) setRevealKey(full);
      fetchKeys();
    } else if (res) {
      // Includes the backend capability denial (e.g. no AUTHCAP465X).
      toast.error(res.message || "Could not create API key");
    }
  };

  const openEdit = async (row) => {
    setEditTarget(row);
    setEditFullAccess(true);
    setEditSelectedCapIds([]);
    setEditLoading(true);
    setEditOpen(true);
    await ensureCapabilities();
    const res = await getMyApiKeyData(keyId(row));
    setEditLoading(false);
    if (res?.status === 200) {
      const data = res.masterapikey_data?.[0];
      setEditFullAccess(Number(data?.FlagAllAccess ?? 1) === 1);
      setEditSelectedCapIds(res.authcapability_id_list ?? []);
    } else if (res) {
      toast.error(res.message || "Could not load API key");
      setEditOpen(false);
    }
  };

  const handleUpdate = async () => {
    const id = keyId(editTarget);
    if (!id) return;
    if (!editFullAccess && editSelectedCapIds.length === 0) {
      toast.error("Pick at least one capability, or switch on Full access");
      return;
    }
    setEditSaving(true);
    const res = await updateMyApiKeys([
      {
        Id: id,
        FlagAllAccess: editFullAccess ? 1 : 0,
        AuthCapabilityIds: editFullAccess ? [] : editSelectedCapIds,
      },
    ]);
    setEditSaving(false);
    if (res?.status === 200) {
      toast.success(res.message || "API key access updated");
      setEditOpen(false);
      setEditTarget(null);
      fetchKeys();
    } else if (res) {
      toast.error(res.message || "Could not update API key access");
    }
  };

  const handleVoid = async () => {
    const id = keyId(deleteTarget);
    if (!id) return;
    setDeleting(true);
    const res = await voidMyApiKeys([id]);
    setDeleting(false);
    if (res?.status === 200) {
      toast.success(res.message || "API key voided");
      setDeleteTarget(null);
      fetchKeys();
    } else if (res) {
      toast.error(res.message || "Could not void API key");
    }
  };

  const copyRevealKey = async () => {
    try {
      await navigator.clipboard.writeText(revealKey);
      toast.success("API key copied to clipboard");
    } catch (e) {
      toast.error("Could not copy — select the key and copy it manually");
    }
  };

  const columns = useMemo(
    () => [
      {
        Header: "Label",
        accessor: "Label",
        filterable: false,
        Cell: (card) => (
          <span className="fw-medium">{card.row.original?.Label || "-"}</span>
        ),
      },
      {
        Header: "Key",
        accessor: "ApiKeyMasked",
        filterable: false,
        Cell: (card) => (
          <code className="text-muted">
            {card.row.original?.ApiKeyMasked || "-"}
          </code>
        ),
      },
      {
        Header: "Access",
        accessor: "FlagAllAccess",
        filterable: false,
        Cell: (card) =>
          Number(card.row.original?.FlagAllAccess) === 1 ? (
            <span className="badge bg-primary-subtle text-primary">
              Full access
            </span>
          ) : (
            <span className="badge bg-warning-subtle text-warning">
              Restricted
            </span>
          ),
      },
      {
        Header: "Created",
        accessor: "CreatedDateTime",
        filterable: false,
        Cell: (card) => (
          <span className="text-muted">
            {formatDateTime(card.row.original?.CreatedDateTime)}
          </span>
        ),
      },
      {
        Header: "Status",
        accessor: "DeletedDateTime",
        filterable: false,
        Cell: (card) =>
          card.row.original?.DeletedDateTime ? (
            <span className="badge bg-danger-subtle text-danger">Voided</span>
          ) : (
            <span className="badge bg-success-subtle text-success">Active</span>
          ),
      },
      {
        Header: "Action",
        filterable: false,
        Cell: (card) => {
          const voided = !!card.row.original?.DeletedDateTime;
          return (
            <div className="d-flex gap-2">
              <Button
                color="soft-secondary"
                size="sm"
                disabled={voided}
                onClick={() => openEdit(card.row.original)}
              >
                <i className="ri-shield-keyhole-line align-bottom" /> Access
              </Button>
              <Button
                color="soft-danger"
                size="sm"
                disabled={voided}
                onClick={() => setDeleteTarget(card.row.original)}
              >
                <i className="ri-delete-bin-line align-bottom" /> Void
              </Button>
            </div>
          );
        },
      },
    ],
    // eslint-disable-next-line react-hooks/exhaustive-deps
    []
  );

  // The Full access toggle + conditional capability picker, reused in the
  // create and edit modals.
  const accessControls = (isFull, setIsFull, ids, setIds, idPrefix) => (
    <div className="mt-3">
      <div className="form-check form-switch">
        <Input
          type="checkbox"
          role="switch"
          id={`${idPrefix}-fullaccess`}
          className="form-check-input"
          checked={isFull}
          onChange={(e) => setIsFull(e.target.checked)}
        />
        <Label htmlFor={`${idPrefix}-fullaccess`} className="form-check-label">
          Full access (can call every endpoint)
        </Label>
      </div>
      {!isFull && (
        <div className="mt-2">
          <Label className="form-label">Allowed capabilities</Label>
          <CapabilityPicker
            capabilities={capabilities ?? []}
            selectedIds={ids}
            onChange={setIds}
          />
          <div className="text-muted fs-12 mt-1">
            The key will be allowed to call only the endpoints for the
            capabilities ticked here (plus minimum-core endpoints).
          </div>
        </div>
      )}
    </div>
  );

  return (
    <SettingsPanel
      title="API Keys"
      description="Programmatic access keys for your company. Create a key, copy it once, scope what it can access, and void keys you no longer need."
      actions={
        <Button color="primary" onClick={openCreate}>
          <i className="ri-add-line align-bottom me-1" /> New API Key
        </Button>
      }
    >
      <Row className="mt-2">
        <Col lg={12}>
          <Card id="apiKeysList">
            <CardBody className="pt-3">
              {loading ? (
                <LoadingSpinner />
              ) : keys.length ? (
                <TableContainer
                  columns={columns}
                  data={keys}
                  isGlobalFilter={false}
                  isAddUserList={false}
                  customPageSize={10}
                  className="custom-header-css"
                  divClass="table-responsive table-card mb-0"
                  tableClass="align-middle table-nowrap"
                  theadClass="text-muted"
                  isLeadsFilter={false}
                />
              ) : (
                <div className="text-muted">
                  You have no API keys yet. Create one to get started.
                </div>
              )}
            </CardBody>
          </Card>
        </Col>
      </Row>

      {/* Create */}
      <AppModal
        open={createOpen}
        onClose={() => setCreateOpen(false)}
        title="Create API Key"
        size="lg"
        loading={creating}
        onSubmit={handleCreate}
        submitLabel="Create"
        submitDisabled={!label.trim()}
      >
        <Label htmlFor="apikey-label" className="form-label">
          Label
        </Label>
        <Input
          id="apikey-label"
          type="text"
          placeholder="e.g. Zapier integration"
          value={label}
          onChange={(e) => setLabel(e.target.value)}
          onKeyDown={(e) => {
            if (e.key === "Enter" && label.trim() && fullAccess) handleCreate();
          }}
        />
        <div className="text-muted fs-12 mt-2">
          A label helps you recognise where this key is used.
        </div>
        {accessControls(
          fullAccess,
          setFullAccess,
          selectedCapIds,
          setSelectedCapIds,
          "create"
        )}
      </AppModal>

      {/* Edit access */}
      <AppModal
        open={editOpen}
        onClose={() => setEditOpen(false)}
        title={`Access — ${editTarget?.Label || "API Key"}`}
        size="lg"
        loading={editSaving}
        onSubmit={handleUpdate}
        submitLabel="Save"
      >
        {editLoading ? (
          <LoadingSpinner />
        ) : (
          <>
            <div className="text-muted fs-12">
              Choose whether this key can access everything, or restrict it to
              specific capabilities.
            </div>
            {accessControls(
              editFullAccess,
              setEditFullAccess,
              editSelectedCapIds,
              setEditSelectedCapIds,
              "edit"
            )}
          </>
        )}
      </AppModal>

      {/* Reveal once */}
      <AppModal
        open={!!revealKey}
        onClose={() => setRevealKey(null)}
        headerAccent="amber"
        title="Copy your API key now"
        size="md"
        onSubmit={() => setRevealKey(null)}
        submitLabel="Done"
        hideCancel
      >
        <p className="mb-2">
          This is the <strong>only time</strong> the full key will be shown.
          Store it somewhere safe — you will not be able to see it again.
        </p>
        <div className="d-flex align-items-center gap-2">
          <code
            className="flex-grow-1 p-2 bg-light rounded text-break user-select-all"
            style={{ wordBreak: "break-all" }}
          >
            {revealKey}
          </code>
          <Button color="primary" onClick={copyRevealKey}>
            <i className="ri-file-copy-line align-bottom me-1" /> Copy
          </Button>
        </div>
      </AppModal>

      {/* Void */}
      <DeleteModal
        show={!!deleteTarget}
        onCloseClick={() => setDeleteTarget(null)}
        onDeleteClick={handleVoid}
        loading={deleting}
        customText={`Void the API key "${
          deleteTarget?.Label || ""
        }"? Any integration using it will immediately stop working.`}
        customButtonText="Yes, Void It"
      />
    </SettingsPanel>
  );
}
